/** Virtual Try-On — Canonical Generation Card (Result-first + collapse + Quick Actions).
 *
 * Vertical order: HEADER → RESULT → INPUT ASSETS → PARAMETERS → PROMPT → FOOTER
 * Collapsed (default browse): HEADER + RESULT only.
 * Expand/collapse uses CanvasBoard `expandedNodeId` (single expanded Generation Card).
 * Result hover Quick Actions reuse `MediaWorkflowRail` + `onQuickGenerate` (artifact ref).
 */

import {
  CloseOutlined,
  CopyOutlined,
  DownloadOutlined,
  HistoryOutlined,
  LoadingOutlined,
  PictureOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import { Handle, Position, useUpdateNodeInternals, type NodeProps } from '@xyflow/react'
import { Tooltip, message } from 'antd'
import CanvasSelect from '../CanvasSelect'
import { memo, useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useCanvasGenerationI18n } from '../useCanvasGenerationI18n'
import { resolveRegenerationDecision } from '../generationRegenerationDecision'
import { computeMediaBoxSize } from '../mediaBoxSize'
import {
  createVirtualTryOnTask,
  getVirtualTryOnTask,
  imageGenerationOutputContentUrl,
  materializeImageGenerationOutput,
  siteAssetVariantUrl,
  virtualTryOnOutputContentUrl,
  type SaasApiError,
  type VirtualTryOnTask,
} from '../../../../api'
import CanvasActionPopover from '../CanvasActionPopover'
import { patchCanvasFieldsIfChanged } from '../canvasFieldsEqual'
import { mediaPreviewHandleTopPx } from '../canvasNodeHandleAnchor'
import {
  buildDefaultProviderSelectionPersistPatch,
  buildExplicitProviderSelectionPatch,
  isExplicitProviderSelectionInvalid,
  normalizeProviderSelectionMode,
} from '../capabilityProviderSelection'
import { loadImagePickerItemDetailDeduped } from '../canvasSharedConfigLoaders'
import {
  TRYON_GARMENT_HANDLE,
  TRYON_PERSON_HANDLE,
  type ConnectedTryOnImage,
} from '../dataflow'
import { functionCardShowsInspector } from '../functionCardInteractionV2'
import { NODE_EXPAND_TRIGGER_CLASS, canvasNodeShellStateClass } from '../canvasNodeShellV2'
import CanvasFunctionCardHeader from '../CanvasFunctionCardHeader'
import { useSingleOrDoubleClick } from '../useSingleOrDoubleClick'
import { useNodeDefinition } from '../nodeRegistry'
import { useCanvasNodeRegistryI18n } from '../nodeRegistryI18n'
import { localizeVirtualTryOnSubmitError, localizeVirtualTryOnTaskError } from '../virtualTryOnErrors'
import {
  TRYON_CLOTHING_CAPABILITY,
  buildVirtualTryOnSubmitPayload,
  findTryOnProviderOption,
  isTryOnProviderOptionSelectable,
  normalizeVirtualTryOnQuality,
  resolveTryOnProviderOptionId,
} from '../virtualTryOnProvider'
import {
  appendTryOnHistoryEntry,
  buildTryOnOverwriteLifecyclePatch,
  buildTryOnRunAcceptancePatch,
  isTryOnExecutionActive,
  resolveTryOnPreviewPresentation,
  type TryOnHistoryEntry,
} from '../virtualTryOnPreviewPresentation'
import {
  computeTryOnDispatchedFingerprint,
  computeTryOnInputFingerprint,
  resolveTryOnEffectiveGarmentAssetId,
  resolveTryOnEffectivePersonAssetId,
} from '../virtualTryOnInputFingerprint'
import {
  inferOutfitScopeControlsFromDisplayName,
  legacyCategoryForScope,
  normalizeOutfitScopeFromFields,
  resolveOutfitScopeControls,
  type OutfitScope,
} from '../virtualTryOnOutfitScope'
import { useCapabilityProviderOptions } from '../useCapabilityProviderOptions'
import MediaLightbox from '../MediaLightbox'
import { downloadCanvasMedia } from '../canvasDownload'
import { resolveTryOnCanvasDisplayUrl, resolveTryOnMasterUrl } from '../canvasImageQuality'
import ImageThumbnailHoverPreview from './ImageThumbnailHoverPreview'
import MediaWorkflowRail from './MediaWorkflowRail'
import type { FlowNodeData } from '../types'
import { generationNodeCardWidth } from '../generationNodeWidthPresets'

const NODE_COLLAPSED_EMPTY_WIDTH = 260
const NODE_COLLAPSED_EMPTY_HEIGHT = 168
const NODE_CARD_WIDTH = generationNodeCardWidth('generate_tryon')
const PROMPT_MAX_LENGTH = 500
const RESULT_MAX_HEIGHT = 400
const RESULT_EMPTY_HEIGHT = 200
const HEADER_HEIGHT = 56
const POLL_INTERVAL_MS = 2500

function mapTaskStatus(status: VirtualTryOnTask['status']): string {
  if (status === 'succeeded') return 'COMPLETED'
  if (status === 'failed') return 'FAILED'
  if (status === 'running') return 'PROCESSING'
  if (status === 'queued') return 'QUEUED'
  return 'READY'
}

async function resolveConnectedAssetId(
  workspaceId: string,
  connected: ConnectedTryOnImage | null | undefined,
  localAssetId: string | null | undefined,
): Promise<string | null> {
  if (connected?.ready) {
    if (connected.entityId) return connected.entityId
    if (connected.generatedOutputId) {
      const res = await materializeImageGenerationOutput(workspaceId, connected.generatedOutputId)
      return res.data.asset_id
    }
    if (connected.tryOnOutputAssetId) return connected.tryOnOutputAssetId
  }
  return localAssetId || null
}

function computeTryOnResultHeight(naturalW: number, naturalH: number, cardInnerWidth: number): number {
  if (!naturalW || !naturalH) return RESULT_EMPTY_HEIGHT
  const ratio = naturalW / naturalH
  let height = cardInnerWidth / ratio
  if (height > RESULT_MAX_HEIGHT) height = RESULT_MAX_HEIGHT
  if (height < 160) height = 160
  return Math.round(height)
}

function GenerateTryOnNodeImpl({ id, data, selected }: NodeProps) {
  const { t, i18n } = useTranslation('canvas')
  const { mediaHeader, regenerate } = useCanvasGenerationI18n()
  const { nodeLabel } = useCanvasNodeRegistryI18n()
  const nodeData = data as unknown as FlowNodeData & {
    workspaceId?: string
    connectedTryOnPerson?: ConnectedTryOnImage | null
    connectedTryOnGarment?: ConnectedTryOnImage | null
    onDuplicateForRegenerate?: (sourceNodeId: string) => void
  }
  const def = useNodeDefinition(nodeData.nodeType)
  const displayLabel = nodeLabel(def.nodeType, def.label)
  const workspaceId = nodeData.workspaceId || ''
  const fields = nodeData.fields || {}

  const isDetailExpanded = Boolean(nodeData.isDetailExpanded)
  const showExpanded = functionCardShowsInspector(isDetailExpanded, nodeData.isDragTempCollapsed)

  const status = (fields.status as string | undefined) ?? 'WAITING'
  const jobId = (fields.jobId as string | undefined) ?? null
  const generatedOutputId = (fields.generatedOutputId as string | undefined) ?? null
  const outputAssetId = (fields.outputAssetId as string | undefined) ?? null
  const previousGeneratedOutputId = (fields.previousGeneratedOutputId as string | undefined) ?? null
  const previousOutputAssetId = (fields.previousOutputAssetId as string | undefined) ?? null
  const { outfitScope, outfitScopeSource, legacyCategory } = normalizeOutfitScopeFromFields(fields)
  const category = legacyCategory
  const quality = normalizeVirtualTryOnQuality(fields.quality)
  const providerSelectionMode = normalizeProviderSelectionMode(fields.providerSelectionMode)
  const storedProviderOptionId = (fields.providerOptionId as string | undefined) ?? null
  const skillSelectionSource = (fields.skillSelectionSource as string | undefined) || 'AUTO'
  const prompt = String(fields.prompt ?? '')
  const personAssetId = (fields.personAssetId as string | undefined) ?? null
  const garmentAssetId = (fields.garmentAssetId as string | undefined) ?? null
  const personLabel = (fields.personLabel as string | undefined) ?? null
  const garmentLabel = (fields.garmentLabel as string | undefined) ?? null
  const errorMessage = (fields.errorMessage as string | null | undefined) ?? null
  const outputWidth = (fields.outputWidth as number | undefined) ?? 0
  const outputHeight = (fields.outputHeight as number | undefined) ?? 0
  const generationHistory = Array.isArray(fields.generationHistory)
    ? (fields.generationHistory as TryOnHistoryEntry[])
    : []

  const connectedPerson = nodeData.connectedTryOnPerson
  const connectedGarment = nodeData.connectedTryOnGarment

  const [previewOpen, setPreviewOpen] = useState(false)
  const [personThumb, setPersonThumb] = useState<string | null>(null)
  const [garmentThumb, setGarmentThumb] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [resultLoadError, setResultLoadError] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [historyBtnEl, setHistoryBtnEl] = useState<HTMLButtonElement | null>(null)
  const [regenerateChoiceOpen, setRegenerateChoiceOpen] = useState(false)
  const [resultZoneActive, setResultZoneActive] = useState(false)
  const [masterMetaExtra, setMasterMetaExtra] = useState<{
    sizeBytes?: number | null
    createdAt?: string | null
    width?: number | null
    height?: number | null
  } | null>(null)
  const [naturalSize, setNaturalSize] = useState({ w: outputWidth || 0, h: outputHeight || 0 })
  const resultZoneLeaveTimerRef = useRef<number | null>(null)
  const runBtnRef = useRef<HTMLButtonElement | null>(null)
  const autoGenerateConsumedRef = useRef(false)
  const preferGeneratingPlaceholderRef = useRef(false)
  const [runHydrating, setRunHydrating] = useState(() => Boolean(jobId && isTryOnExecutionActive(status)))
  const {
    options: providerOptions,
    error: providerOptionsError,
    defaultProviderOptionId,
    defaultOption: defaultProviderOption,
  } = useCapabilityProviderOptions(workspaceId, TRYON_CLOTHING_CAPABILITY, 'generate_tryon')
  const submittingRef = useRef(false)
  const pollTimerRef = useRef<number | null>(null)
  const staleJobReconcileRef = useRef<string | null>(null)
  const fieldsRef = useRef(fields)
  fieldsRef.current = fields
  const updateNodeInternals = useUpdateNodeInternals()

  const activateResultZone = useCallback(() => {
    if (resultZoneLeaveTimerRef.current != null) {
      window.clearTimeout(resultZoneLeaveTimerRef.current)
      resultZoneLeaveTimerRef.current = null
    }
    setResultZoneActive(true)
  }, [])

  const deactivateResultZone = useCallback(() => {
    if (resultZoneLeaveTimerRef.current != null) {
      window.clearTimeout(resultZoneLeaveTimerRef.current)
    }
    resultZoneLeaveTimerRef.current = window.setTimeout(() => {
      setResultZoneActive(false)
      resultZoneLeaveTimerRef.current = null
    }, 120)
  }, [])

  useEffect(
    () => () => {
      if (resultZoneLeaveTimerRef.current != null) {
        window.clearTimeout(resultZoneLeaveTimerRef.current)
      }
    },
    [],
  )

  const resolvedProviderOptionId = useMemo(
    () =>
      resolveTryOnProviderOptionId(
        storedProviderOptionId,
        providerOptions,
        providerSelectionMode,
        defaultProviderOptionId,
      ),
    [defaultProviderOptionId, providerOptions, providerSelectionMode, storedProviderOptionId],
  )
  const selectedProvider = useMemo(
    () => findTryOnProviderOption(providerOptions, resolvedProviderOptionId),
    [providerOptions, resolvedProviderOptionId],
  )
  const explicitProviderInvalid = useMemo(
    () =>
      isExplicitProviderSelectionInvalid({
        selectionMode: providerSelectionMode,
        storedProviderOptionId,
        options: providerOptions,
      }),
    [providerOptions, providerSelectionMode, storedProviderOptionId],
  )
  const outfitScopeControls = useMemo(() => {
    const fromApi = resolveOutfitScopeControls(selectedProvider)
    if (selectedProvider?.outfit_scope_controls) return fromApi
    const inferred = inferOutfitScopeControlsFromDisplayName(selectedProvider?.frontend_display_name)
    return inferred ? resolveOutfitScopeControls({ outfit_scope_controls: inferred }) : fromApi
  }, [selectedProvider])
  const lastDispatchedPersonAssetId = (fields.lastDispatchedPersonAssetId as string | undefined) ?? null
  const lastDispatchedGarmentAssetId = (fields.lastDispatchedGarmentAssetId as string | undefined) ?? null

  const materialInputs = useMemo(
    () => ({
      personAssetId,
      garmentAssetId,
      connectedPersonEntityId: connectedPerson?.ready ? connectedPerson.entityId || null : null,
      connectedGarmentEntityId: connectedGarment?.ready ? connectedGarment.entityId || null : null,
      connectedPersonGeneratedOutputId: connectedPerson?.ready ? connectedPerson.generatedOutputId || null : null,
      connectedGarmentGeneratedOutputId: connectedGarment?.ready ? connectedGarment.generatedOutputId || null : null,
      connectedPersonTryOnOutputAssetId: connectedPerson?.ready ? connectedPerson.tryOnOutputAssetId || null : null,
      connectedGarmentTryOnOutputAssetId: connectedGarment?.ready ? connectedGarment.tryOnOutputAssetId || null : null,
      connectedPersonTryOnOutputId: connectedPerson?.ready ? connectedPerson.tryOnOutputId || null : null,
      connectedGarmentTryOnOutputId: connectedGarment?.ready ? connectedGarment.tryOnOutputId || null : null,
      lastDispatchedPersonAssetId,
      lastDispatchedGarmentAssetId,
      outfitScope,
      category,
      quality,
      providerOptionId: resolvedProviderOptionId,
      prompt,
    }),
    [
      outfitScope,
      category,
      connectedGarment,
      connectedPerson,
      garmentAssetId,
      lastDispatchedGarmentAssetId,
      lastDispatchedPersonAssetId,
      personAssetId,
      prompt,
      quality,
      resolvedProviderOptionId,
    ],
  )
  const currentInputFingerprint = useMemo(() => computeTryOnInputFingerprint(materialInputs), [materialInputs])
  const resultInputFingerprint = (fields.resultInputFingerprint as string | undefined) ?? null
  const materialInputsChanged =
    Boolean(fields.needsRegeneration) ||
    (Boolean(resultInputFingerprint) && resultInputFingerprint !== currentInputFingerprint)

  const presentation = resolveTryOnPreviewPresentation({
    status,
    needsRegeneration: Boolean(fields.needsRegeneration) || materialInputsChanged,
    outputAssetId,
    generatedOutputId,
    previousOutputAssetId,
    previousGeneratedOutputId,
    runHydrating,
    preferGeneratingPlaceholder: preferGeneratingPlaceholderRef.current,
  })
  const needsRegeneration = Boolean(fields.needsRegeneration) || materialInputsChanged
  const isBusy =
    isTryOnExecutionActive(status) ||
    (runHydrating && presentation.mode !== 'COMPLETED_CURRENT' && presentation.mode !== 'COMPLETED_STALE')
  const hasPerson = Boolean(connectedPerson?.ready || personAssetId)
  const hasGarment = Boolean(connectedGarment?.ready || garmentAssetId)
  const providerReady =
    Boolean(resolvedProviderOptionId) &&
    !explicitProviderInvalid &&
    (!selectedProvider || isTryOnProviderOptionSelectable(selectedProvider))
  const canGenerate = hasPerson && hasGarment && providerReady && !isBusy && !providerOptionsError
  const hasCurrentResult = Boolean(outputAssetId || generatedOutputId)
  const showPreviewMedia = presentation.showActiveResult || presentation.showStaleResult
  const previewOutputAssetId = presentation.previewOutputAssetId
  const previewGeneratedOutputId = presentation.previewGeneratedOutputId

  useEffect(() => {
    setMasterMetaExtra(null)
    if (!workspaceId || !previewOutputAssetId) return
    let cancelled = false
    loadImagePickerItemDetailDeduped(workspaceId, `sa:${previewOutputAssetId}`, {
      include_usage: false,
      include_variants: false,
    })
      .then((detail) => {
        if (cancelled) return
        setMasterMetaExtra({
          width: detail.width,
          height: detail.height,
          sizeBytes: detail.size_bytes,
          createdAt: detail.created_at || detail.ai_provenance?.generated_at || null,
        })
      })
      .catch(() => {
        /* Metadata is best-effort — viewer still works without bytes/time. */
      })
    return () => {
      cancelled = true
    }
  }, [workspaceId, previewOutputAssetId])

  const collapsedMediaBox = useMemo(() => {
    if (!showPreviewMedia && !presentation.showGenerating) {
      return { width: NODE_COLLAPSED_EMPTY_WIDTH, height: NODE_COLLAPSED_EMPTY_HEIGHT }
    }
    const w = naturalSize.w || outputWidth || 768
    const h = naturalSize.h || outputHeight || 1024
    return computeMediaBoxSize(w, h)
  }, [naturalSize.h, naturalSize.w, outputHeight, outputWidth, presentation.showGenerating, showPreviewMedia])

  const cardWidth = showExpanded ? NODE_CARD_WIDTH : collapsedMediaBox.width
  const cardInnerWidth = showExpanded ? NODE_CARD_WIDTH - 20 : Math.max(1, collapsedMediaBox.width - 2)
  const resultHeight = useMemo(() => {
    if (!showExpanded) {
      return presentation.showGenerating && !showPreviewMedia
        ? NODE_COLLAPSED_EMPTY_HEIGHT
        : collapsedMediaBox.height
    }
    if (showPreviewMedia || presentation.showGenerating) {
      return computeTryOnResultHeight(naturalSize.w || outputWidth || 768, naturalSize.h || outputHeight || 1024, cardInnerWidth)
    }
    return RESULT_EMPTY_HEIGHT
  }, [
    cardInnerWidth,
    collapsedMediaBox.height,
    naturalSize.h,
    naturalSize.w,
    outputHeight,
    outputWidth,
    presentation.showGenerating,
    showExpanded,
    showPreviewMedia,
  ])

  const outputHandleTop = mediaPreviewHandleTopPx(resultHeight, HEADER_HEIGHT)
  const personHandleTop = showExpanded ? HEADER_HEIGHT + resultHeight + 56 : outputHandleTop
  const garmentHandleTop = showExpanded ? personHandleTop + 72 : outputHandleTop

  const resultUrl = resolveTryOnCanvasDisplayUrl(workspaceId, previewOutputAssetId, previewGeneratedOutputId)
  const masterUrl = resolveTryOnMasterUrl(workspaceId, previewOutputAssetId, previewGeneratedOutputId)

  const handleDownloadMaster = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation()
      if (!masterUrl) return
      downloadCanvasMedia(masterUrl)
    },
    [masterUrl],
  )

  useEffect(() => {
    if (outputWidth > 0 && outputHeight > 0) {
      setNaturalSize((prev) => (prev.w === outputWidth && prev.h === outputHeight ? prev : { w: outputWidth, h: outputHeight }))
    }
  }, [outputHeight, outputWidth])

  useEffect(() => {
    setResultLoadError(false)
  }, [resultUrl])

  const providerSelectOptions = useMemo(
    () =>
      providerOptions.map((opt) => {
        const available = isTryOnProviderOptionSelectable(opt)
        const reasonKey = opt.availability_reason
          ? `virtualTryOn.provider.reasons.${opt.availability_reason}`
          : null
        const reasonLabel =
          !available && reasonKey
            ? t(reasonKey, {
                defaultValue: t('virtualTryOn.provider.unavailable', { defaultValue: 'Unavailable' }),
              })
            : !available
              ? t('virtualTryOn.provider.unavailable', { defaultValue: 'Unavailable' })
              : null
        return {
          value: opt.provider_option_id,
          disabled: !available,
          label: opt.frontend_display_name,
          title: [opt.frontend_description, reasonLabel].filter(Boolean).join(' — ') || opt.frontend_display_name,
          description: opt.frontend_description,
          engineLabel: opt.engine_label || opt.product_meta?.engine_label || null,
          reasonLabel,
        }
      }),
    [providerOptions, t],
  )
  const recommendedProviderLabel =
    defaultProviderOption?.frontend_display_name ||
    t('virtualTryOn.provider.placeholder', { defaultValue: 'Select provider' })
  const providerParameterHints = useMemo(() => {
    const schema = selectedProvider?.parameter_schema
    if (!schema || typeof schema !== 'object') return [] as string[]
    return Object.keys(schema).filter((k) => !['model', 'model_name'].includes(k))
  }, [selectedProvider])

  const providerInputConstraintHint = useMemo(() => {
    const c = selectedProvider?.input_constraints
    if (!c) return null
    const zh = (i18n.language || '').toLowerCase().startsWith('zh')
    return (zh ? c.hint_zh : c.hint_en) || c.hint_en || c.hint_zh || null
  }, [i18n.language, selectedProvider])

  const resultStatusBadge = presentation.showGenerating
    ? t('virtualTryOn.preview.generating')
    : status === 'FAILED'
      ? t('virtualTryOn.status.failed')
      : needsRegeneration && showPreviewMedia
        ? t('generation.needsRegeneration')
        : showPreviewMedia
          ? t('virtualTryOn.result.ready', { defaultValue: '已生成' })
          : t('virtualTryOn.result.empty', { defaultValue: '未生成' })

  const patchFields = useCallback(
    (patch: Record<string, unknown>) => {
      const next = patchCanvasFieldsIfChanged(fields as Record<string, unknown>, patch)
      if (!next) return
      nodeData.onChangeFields?.(id, next)
    },
    [fields, id, nodeData],
  )

  const markStaleIfHasResult = useCallback(
    (patch: Record<string, unknown>) => {
      const hadResult = Boolean(outputAssetId || generatedOutputId || previousOutputAssetId || previousGeneratedOutputId)
      return hadResult ? { ...patch, needsRegeneration: true } : patch
    },
    [generatedOutputId, outputAssetId, previousGeneratedOutputId, previousOutputAssetId],
  )

  // Soft-migrate legacy implicit tops/dress → FULL_LOOK_AUTO without clearing current result.
  useEffect(() => {
    if (
      fields.outfitScope === outfitScope &&
      fields.outfitScopeSource === outfitScopeSource &&
      fields.category === legacyCategory
    ) {
      return
    }
    patchFields({
      outfitScope,
      outfitScopeSource,
      category: legacyCategory,
    })
  }, [
    fields.category,
    fields.outfitScope,
    fields.outfitScopeSource,
    legacyCategory,
    outfitScope,
    outfitScopeSource,
    patchFields,
  ])

  const clearPoll = useCallback(() => {
    if (pollTimerRef.current != null) {
      window.clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  const pollTask = useCallback(
    (taskId: string) => {
      clearPoll()
      const tick = async () => {
        try {
          const live = fieldsRef.current as Record<string, unknown>
          const liveJobId = (live.jobId as string | null | undefined) ?? null
          // Attempt fencing: late callbacks from a previous run must not apply.
          if (!liveJobId || liveJobId !== taskId) return
          const res = await getVirtualTryOnTask(workspaceId, taskId)
          const mapped = mapTaskStatus(res.status)
          const output = res.output
          const livePrevAsset = (live.previousOutputAssetId as string | null | undefined) ?? null
          const livePrevGen = (live.previousGeneratedOutputId as string | null | undefined) ?? null
          const liveHistory = Array.isArray(live.generationHistory)
            ? (live.generationHistory as TryOnHistoryEntry[])
            : []
          const liveW = (live.outputWidth as number | undefined) ?? outputWidth
          const liveH = (live.outputHeight as number | undefined) ?? outputHeight
          const failDisplay =
            mapped === 'FAILED'
              ? localizeVirtualTryOnTaskError(
                  res.error_code || res.external_provider_run?.error_code || null,
                  t,
                )
              : null
          const patch: Record<string, unknown> = {
            status: mapped,
            errorMessage: failDisplay,
          }
          if (mapped === 'COMPLETED') {
            preferGeneratingPlaceholderRef.current = false
            // History already received old current on overwrite start; only append
            // leftover previous* if any (legacy paths).
            if (livePrevAsset || livePrevGen) {
              patch.generationHistory = appendTryOnHistoryEntry(liveHistory, {
                outputAssetId: livePrevAsset,
                generatedOutputId: livePrevGen,
                outputWidth: liveW,
                outputHeight: liveH,
              })
            }
            patch.generatedOutputId = output?.output_id ?? null
            patch.outputAssetId = output?.site_asset_id ?? null
            patch.outputWidth = output?.width ?? liveW
            patch.outputHeight = output?.height ?? liveH
            patch.previousGeneratedOutputId = null
            patch.previousOutputAssetId = null
            patch.needsRegeneration = false
            const dispatchedPerson =
              (live.lastDispatchedPersonAssetId as string | null | undefined) ||
              (live.personAssetId as string | null | undefined) ||
              null
            const dispatchedGarment =
              (live.lastDispatchedGarmentAssetId as string | null | undefined) ||
              (live.garmentAssetId as string | null | undefined) ||
              null
            patch.resultInputFingerprint =
              (live.resultInputFingerprint as string | undefined) ||
              (dispatchedPerson && dispatchedGarment
                ? computeTryOnDispatchedFingerprint({
                    personAssetId: dispatchedPerson,
                    garmentAssetId: dispatchedGarment,
                    category: (live.category as string | null) ?? null,
                    quality: (live.quality as string | null) ?? null,
                    providerOptionId: (live.providerOptionId as string | null) ?? null,
                    prompt: (live.prompt as string | null) ?? null,
                  })
                : computeTryOnInputFingerprint({
                    personAssetId: (live.personAssetId as string | null) ?? null,
                    garmentAssetId: (live.garmentAssetId as string | null) ?? null,
                    category: (live.category as string | null) ?? null,
                    quality: (live.quality as string | null) ?? null,
                    providerOptionId: (live.providerOptionId as string | null) ?? null,
                    prompt: (live.prompt as string | null) ?? null,
                  }))
          } else if (mapped === 'FAILED') {
            preferGeneratingPlaceholderRef.current = false
            // Keep History intact; do not silently restore old result as COMPLETED current.
            patch.generatedOutputId = null
            patch.outputAssetId = null
            patch.previousOutputAssetId = null
            patch.previousGeneratedOutputId = null
            patch.needsRegeneration = true
          }
          // Re-check fencing after await
          const latestJobId = ((fieldsRef.current as Record<string, unknown>).jobId as string | null) ?? null
          if (!latestJobId || latestJobId !== taskId) return
          patchFields(patch)
          if (mapped === 'COMPLETED' || mapped === 'FAILED') {
            if (mapped === 'FAILED') message.error(failDisplay || t('virtualTryOn.errors.processingFailed'))
            return
          }
          pollTimerRef.current = window.setTimeout(tick, POLL_INTERVAL_MS)
        } catch {
          pollTimerRef.current = window.setTimeout(tick, POLL_INTERVAL_MS * 2)
        }
      }
      void tick()
    },
    [clearPoll, outputHeight, outputWidth, patchFields, t, workspaceId],
  )

  const patchFieldsRef = useRef(patchFields)
  patchFieldsRef.current = patchFields
  const pollTaskRef = useRef(pollTask)
  pollTaskRef.current = pollTask

  useEffect(() => () => clearPoll(), [clearPoll])

  useEffect(() => {
    if (!jobId || !workspaceId) {
      setRunHydrating(false)
      return
    }
    if (status === 'QUEUED' || status === 'PROCESSING') {
      staleJobReconcileRef.current = null
      pollTaskRef.current(jobId)
      setRunHydrating(false)
      return
    }
    if (status === 'COMPLETED' && (outputAssetId || generatedOutputId)) {
      if (staleJobReconcileRef.current === jobId) return
      staleJobReconcileRef.current = jobId
      let cancelled = false
      getVirtualTryOnTask(workspaceId, jobId)
        .then((res) => {
          if (cancelled) return
          const mapped = mapTaskStatus(res.status)
          if (mapped === 'QUEUED' || mapped === 'PROCESSING') {
            const ids = { outputAssetId, generatedOutputId }
            patchFieldsRef.current({
              status: mapped,
              ...buildTryOnRunAcceptancePatch(ids),
            })
            pollTaskRef.current(jobId)
          }
        })
        .catch(() => {})
      return () => {
        cancelled = true
      }
    }
    setRunHydrating(false)
  }, [generatedOutputId, jobId, outputAssetId, status, workspaceId])

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => updateNodeInternals(id))
    return () => window.cancelAnimationFrame(frame)
  }, [id, resultHeight, showExpanded, showPreviewMedia, presentation.showGenerating, updateNodeInternals])

  useEffect(() => {
    let cancelled = false
    setPersonThumb(null)
    if (connectedPerson?.ready) {
      if (connectedPerson.entityId && workspaceId) {
        loadImagePickerItemDetailDeduped(workspaceId, `sa:${connectedPerson.entityId}`, { include_usage: false, include_variants: false })
          .then((d) => {
            if (!cancelled) setPersonThumb(d.thumbnail_url || d.preview_url || null)
          })
          .catch(() => {})
      } else if (connectedPerson.generatedOutputId && workspaceId) {
        setPersonThumb(imageGenerationOutputContentUrl(workspaceId, connectedPerson.generatedOutputId))
      } else if (connectedPerson.tryOnOutputAssetId && workspaceId) {
        setPersonThumb(siteAssetVariantUrl(workspaceId, connectedPerson.tryOnOutputAssetId, 'thumbnail'))
      } else if (connectedPerson.tryOnOutputId && workspaceId) {
        setPersonThumb(virtualTryOnOutputContentUrl(workspaceId, connectedPerson.tryOnOutputId))
      }
      return () => {
        cancelled = true
      }
    }
    if (personAssetId && workspaceId) {
      loadImagePickerItemDetailDeduped(workspaceId, `sa:${personAssetId}`, { include_usage: false, include_variants: false })
        .then((d) => {
          if (!cancelled) setPersonThumb(d.thumbnail_url || d.preview_url || null)
        })
        .catch(() => {})
    }
    return () => {
      cancelled = true
    }
  }, [connectedPerson, personAssetId, workspaceId])

  useEffect(() => {
    let cancelled = false
    setGarmentThumb(null)
    if (connectedGarment?.ready) {
      if (connectedGarment.entityId && workspaceId) {
        loadImagePickerItemDetailDeduped(workspaceId, `sa:${connectedGarment.entityId}`, { include_usage: false, include_variants: false })
          .then((d) => {
            if (!cancelled) setGarmentThumb(d.thumbnail_url || d.preview_url || null)
          })
          .catch(() => {})
      } else if (connectedGarment.generatedOutputId && workspaceId) {
        setGarmentThumb(imageGenerationOutputContentUrl(workspaceId, connectedGarment.generatedOutputId))
      } else if (connectedGarment.tryOnOutputAssetId && workspaceId) {
        setGarmentThumb(siteAssetVariantUrl(workspaceId, connectedGarment.tryOnOutputAssetId, 'thumbnail'))
      } else if (connectedGarment.tryOnOutputId && workspaceId) {
        setGarmentThumb(virtualTryOnOutputContentUrl(workspaceId, connectedGarment.tryOnOutputId))
      }
      return () => {
        cancelled = true
      }
    }
    if (garmentAssetId && workspaceId) {
      loadImagePickerItemDetailDeduped(workspaceId, `sa:${garmentAssetId}`, { include_usage: false, include_variants: false })
        .then((d) => {
          if (!cancelled) setGarmentThumb(d.thumbnail_url || d.preview_url || null)
        })
        .catch(() => {})
    }
    return () => {
      cancelled = true
    }
  }, [connectedGarment, garmentAssetId, workspaceId])

  const openPersonPicker = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onOpenTryOnAssetPicker?.({
      nodeId: id,
      slot: 'person',
      title: hasPerson ? t('virtualTryOn.person.replace') : t('virtualTryOn.person.select'),
      initialSelection: personAssetId || connectedPerson?.entityId || null,
    })
  }

  const openGarmentPicker = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onOpenTryOnAssetPicker?.({
      nodeId: id,
      slot: 'garment',
      title: t('virtualTryOn.garment.select'),
      initialSelection: garmentAssetId,
    })
  }

  const runGenerate = useCallback(async () => {
    if (!workspaceId || submittingRef.current || !canGenerate) return
    if (!resolvedProviderOptionId) {
      message.error(t('virtualTryOn.errors.providerRequired', { defaultValue: 'Please select a Provider' }))
      return
    }
    submittingRef.current = true
    setSubmitError(null)
    const selectionPersist =
      providerSelectionMode === 'EXPLICIT'
        ? buildExplicitProviderSelectionPatch(resolvedProviderOptionId)
        : buildDefaultProviderSelectionPersistPatch()
    const hadResult = Boolean(outputAssetId || generatedOutputId)
    const preResolveFingerprint = computeTryOnInputFingerprint(materialInputs)
    if (hadResult) {
      preferGeneratingPlaceholderRef.current = true
      patchFields({
        ...buildTryOnOverwriteLifecyclePatch({
          outputAssetId,
          generatedOutputId,
          outputWidth,
          outputHeight,
          generationHistory,
          personAssetId: resolveTryOnEffectivePersonAssetId(materialInputs),
          garmentAssetId: resolveTryOnEffectiveGarmentAssetId(materialInputs),
          providerOptionId: resolvedProviderOptionId,
          quality,
          category,
          outfitScope,
          prompt,
          jobId,
          inputFingerprint: resultInputFingerprint || preResolveFingerprint,
        }),
        ...selectionPersist,
      })
    } else {
      preferGeneratingPlaceholderRef.current = false
      patchFields({ status: 'SUBMITTING', errorMessage: null, jobId: null, ...selectionPersist })
    }
    try {
      const modelId = await resolveConnectedAssetId(workspaceId, connectedPerson, personAssetId)
      const garmentId = await resolveConnectedAssetId(workspaceId, connectedGarment, garmentAssetId)
      if (!modelId || !garmentId) {
        throw new Error(t('virtualTryOn.errors.invalidInput'))
      }
      // Authoritative fingerprint uses post-materialize SiteAsset ids (what FASHN receives).
      const dispatchFingerprint = computeTryOnDispatchedFingerprint({
        personAssetId: modelId,
        garmentAssetId: garmentId,
        outfitScope,
        category,
        quality,
        providerOptionId: resolvedProviderOptionId,
        prompt,
      })
      const res = await createVirtualTryOnTask(
        workspaceId,
        buildVirtualTryOnSubmitPayload({
          modelImageAssetId: modelId,
          garmentAssetId: garmentId,
          outfitScope,
          outfitScopeSource,
          category,
          quality,
          providerOptionId: resolvedProviderOptionId,
          providerSelectionMode,
          prompt,
        }),
      )
      const mappedStatus = mapTaskStatus(res.status)
      const immediateFail =
        mappedStatus === 'FAILED'
          ? localizeVirtualTryOnTaskError(
              (res as { error_code?: string | null }).error_code ||
                ((res as { error?: { code?: string } }).error?.code ?? null),
              t,
            )
          : null
      patchFields({
        jobId: res.job_id,
        status: mappedStatus,
        ...selectionPersist,
        // Keep picker ids null when edge-bound, but always persist dispatched ids for forensic/fingerprint.
        personAssetId: connectedPerson?.ready ? null : modelId,
        garmentAssetId: connectedGarment?.ready ? null : garmentId,
        lastDispatchedPersonAssetId: modelId,
        lastDispatchedGarmentAssetId: garmentId,
        needsRegeneration: false,
        resultInputFingerprint: dispatchFingerprint,
        outputAssetId: null,
        generatedOutputId: null,
        previousOutputAssetId: null,
        previousGeneratedOutputId: null,
        errorMessage: immediateFail,
      })
      if (mappedStatus === 'FAILED') {
        preferGeneratingPlaceholderRef.current = false
        message.error(immediateFail || t('virtualTryOn.errors.processingFailed'))
        return
      }
      pollTask(res.job_id)
    } catch (err) {
      preferGeneratingPlaceholderRef.current = false
      const apiErr = err as SaasApiError
      const display = localizeVirtualTryOnSubmitError(apiErr, t)
      setSubmitError(display)
      patchFields({
        status: 'FAILED',
        needsRegeneration: true,
        errorMessage: display,
        jobId: null,
        outputAssetId: null,
        generatedOutputId: null,
      })
      message.error(display)
    } finally {
      submittingRef.current = false
    }
  }, [
    canGenerate,
    category,
    outfitScope,
    outfitScopeSource,
    quality,
    connectedGarment,
    connectedPerson,
    garmentAssetId,
    generatedOutputId,
    generationHistory,
    jobId,
    materialInputs,
    outputAssetId,
    outputHeight,
    outputWidth,
    patchFields,
    personAssetId,
    pollTask,
    prompt,
    providerSelectionMode,
    resolvedProviderOptionId,
    resultInputFingerprint,
    t,
    workspaceId,
  ])

  const handleGenerateClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    if (isBusy) return
    if (!canGenerate) {
      if (!hasPerson || !hasGarment) {
        message.warning(t('virtualTryOn.errors.invalidInput'))
      }
      return
    }
    const decision = resolveRegenerationDecision({
      hasCurrentResult,
      materialInputsChanged: needsRegeneration,
      isBusy,
      isFailedRetry: status === 'FAILED' && !needsRegeneration,
    })
    if (decision === 'ask') {
      setRegenerateChoiceOpen(true)
      return
    }
    void runGenerate()
  }

  const handleOverwriteRegenerate = useCallback(() => {
    setRegenerateChoiceOpen(false)
    void runGenerate()
  }, [runGenerate])

  const handleRegenerateSeparately = useCallback(() => {
    setRegenerateChoiceOpen(false)
    nodeData.onDuplicateForRegenerate?.(id)
  }, [id, nodeData])

  useEffect(() => {
    if (!fields.pendingAutoGenerate || isBusy || autoGenerateConsumedRef.current) return
    if (!canGenerate) return
    autoGenerateConsumedRef.current = true
    patchFields({ pendingAutoGenerate: false })
    void runGenerate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.pendingAutoGenerate, isBusy, canGenerate])

  const handleCopyClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onDuplicateNode?.(id)
  }
  const handleDeleteClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    nodeData.onRequestDelete?.(id, e.currentTarget)
  }

  const restoreHistoryEntry = (entry: TryOnHistoryEntry) => {
    if (showPreviewMedia && (outputAssetId || generatedOutputId)) {
      patchFields({
        generationHistory: appendTryOnHistoryEntry(
          generationHistory.filter((h) => h.version !== entry.version),
          {
            outputAssetId,
            generatedOutputId,
            outputWidth,
            outputHeight,
          },
        ),
        outputAssetId: entry.outputAssetId,
        generatedOutputId: entry.generatedOutputId,
        outputWidth: entry.outputWidth ?? outputWidth,
        outputHeight: entry.outputHeight ?? outputHeight,
        status: 'COMPLETED',
        needsRegeneration: false,
      })
    } else {
      patchFields({
        outputAssetId: entry.outputAssetId,
        generatedOutputId: entry.generatedOutputId,
        outputWidth: entry.outputWidth ?? outputWidth,
        outputHeight: entry.outputHeight ?? outputHeight,
        status: 'COMPLETED',
        needsRegeneration: false,
        generationHistory: generationHistory.filter((h) => h.version !== entry.version),
      })
    }
    setHistoryOpen(false)
  }

  const { onClick: onMediaClick, onDoubleClick: onMediaDoubleClick } = useSingleOrDoubleClick(
    () => {
      nodeData.onExpandFunctionCardDetail?.(id)
    },
    () => {
      if (showPreviewMedia && resultUrl) setPreviewOpen(true)
    },
  )

  const personDisplayLabel =
    connectedPerson?.ready
      ? connectedPerson.label
      : personLabel || (personAssetId ? t('virtualTryOn.person.connected') : t('virtualTryOn.person.waiting'))

  const garmentDisplayLabel =
    connectedGarment?.ready
      ? connectedGarment.label
      : garmentLabel || (garmentAssetId ? t('virtualTryOn.garment.connected') : t('virtualTryOn.garment.waiting'))

  return (
    <div
      className={`pcv-node pcv-node--media pcv-node--tryon-generator pcv-node--generate-tryon${selected ? ' is-selected' : ''} ${canvasNodeShellStateClass(showExpanded)}${resultZoneActive ? ' is-result-zone-active' : ''}`}
      style={{ width: cardWidth }}
      data-testid="canvas-tryon-node"
      data-expanded={showExpanded ? 'yes' : 'no'}
    >
      <Handle
        type="target"
        position={Position.Left}
        id={TRYON_PERSON_HANDLE}
        className="pcv-handle pcv-handle--lg"
        style={{ top: personHandleTop }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id={TRYON_GARMENT_HANDLE}
        className="pcv-handle pcv-handle--lg"
        style={{ top: garmentHandleTop }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="image-out"
        className="pcv-handle pcv-handle--lg"
        style={{ top: outputHandleTop }}
      />

      {/* ① Header — icon + title (+ runtime status); keep actions */}
      <CanvasFunctionCardHeader
        icon={def.icon}
        color={def.color}
        title={displayLabel}
        statusBadge={
          needsRegeneration && showPreviewMedia && !isBusy ? (
            <span className="pcv-brief-header-badge pcv-gen-stale-badge" data-testid="tryon-needs-regen-badge">
              {t('generation.needsRegeneration')}
            </span>
          ) : null
        }
        actions={
          <>
            {showPreviewMedia && masterUrl ? (
              <Tooltip title={t('mediaLightbox.downloadOriginal', { defaultValue: '下载原图' })} mouseEnterDelay={0.3}>
                <button
                  type="button"
                  className="pcv-media-hover-btn"
                  aria-label={t('mediaLightbox.downloadOriginal', { defaultValue: '下载原图' })}
                  data-testid="tryon-download-original"
                  onClick={handleDownloadMaster}
                >
                  <DownloadOutlined />
                </button>
              </Tooltip>
            ) : null}
            <Tooltip title={t('virtualTryOn.history.title', { defaultValue: '历史版本' })} mouseEnterDelay={0.3}>
              <button
                type="button"
                ref={setHistoryBtnEl}
                className="pcv-media-hover-btn"
                data-testid="tryon-history-btn"
                onClick={(e) => {
                  e.stopPropagation()
                  setHistoryBtnEl(e.currentTarget)
                  setHistoryOpen(true)
                }}
              >
                <HistoryOutlined />
              </button>
            </Tooltip>
            <Tooltip title={mediaHeader.duplicate} mouseEnterDelay={0.3}>
              <button type="button" className="pcv-media-hover-btn" aria-label={mediaHeader.duplicate} onClick={handleCopyClick}>
                <CopyOutlined />
              </button>
            </Tooltip>
            <Tooltip title={mediaHeader.delete} mouseEnterDelay={0.3}>
              <button type="button" className="pcv-media-hover-btn pcv-media-hover-btn--danger" aria-label={mediaHeader.delete} onClick={handleDeleteClick}>
                <CloseOutlined />
              </button>
            </Tooltip>
          </>
        }
      />

      {/* ② Result — always visible; ONE image only */}
      <div
        className={`pcv-gen-result-area pcv-gen-tryon-result-area ${NODE_EXPAND_TRIGGER_CLASS}`}
        data-testid="tryon-result-area"
        onMouseEnter={activateResultZone}
        onMouseLeave={deactivateResultZone}
        onClick={onMediaClick}
        onDoubleClick={onMediaDoubleClick}
      >
        <div className="pcv-gen-result-section-label">
          <span>{t('virtualTryOn.result.label', { defaultValue: '生成结果' })}</span>
          <span
            className={`pcv-gen-result-status-badge${showPreviewMedia && !isBusy ? ' is-ready' : ''}${presentation.showGenerating ? ' is-busy' : ''}`}
          >
            {resultStatusBadge}
          </span>
        </div>

        <div className="pcv-tryon-result-frame" style={{ height: resultHeight }}>
          {!showPreviewMedia && !presentation.showGenerating ? (
            <div className="pcv-gen-result-empty" data-testid="tryon-result-empty">
              <span className="pcv-gen-result-empty-icon">{def.icon}</span>
              <span>{t('virtualTryOn.preview.empty')}</span>
              <span className="pcv-gen-result-empty-hint">
                {showExpanded
                  ? t('virtualTryOn.result.emptyHintExpanded', {
                      defaultValue: '请添加人物与服装后点击「生成换装」',
                    })
                  : t('virtualTryOn.result.emptyHintCollapsed', {
                      defaultValue: '展开卡片添加人物与服装，再点击「生成换装」',
                    })}
              </span>
            </div>
          ) : null}

          {showPreviewMedia && resultUrl && !resultLoadError ? (
            <div className="pcv-tryon-result-image-wrap">
              {masterUrl ? (
                <div className="pcv-gen-result-download nodrag" data-testid="tryon-result-download">
                  <Tooltip title={t('mediaLightbox.downloadOriginal', { defaultValue: '下载原图' })}>
                    <button
                      type="button"
                      className="pcv-media-hover-btn"
                      aria-label={t('mediaLightbox.downloadOriginal', { defaultValue: '下载原图' })}
                      onClick={handleDownloadMaster}
                    >
                      <DownloadOutlined />
                    </button>
                  </Tooltip>
                </div>
              ) : null}
              <img
                src={resultUrl}
                alt=""
                className="pcv-tryon-result-image"
                draggable={false}
                data-testid="tryon-current-result-image"
                data-quality-role="display_preview"
                onError={() => setResultLoadError(true)}
                onLoad={(e) => {
                  const img = e.currentTarget
                  if (img.naturalWidth && img.naturalHeight) {
                    setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight })
                    // Display preview dimensions must not overwrite MASTER metadata when present.
                    if (!outputWidth || !outputHeight) {
                      if (img.naturalWidth !== outputWidth || img.naturalHeight !== outputHeight) {
                        patchFields({ outputWidth: img.naturalWidth, outputHeight: img.naturalHeight })
                      }
                    }
                  }
                }}
              />
              {presentation.showGenerating ? (
                <div className="pcv-tryon-result-overlay" data-testid="tryon-generating-overlay">
                  <LoadingOutlined spin />
                  <span>{resultStatusBadge}</span>
                </div>
              ) : null}
            </div>
          ) : null}

          {!showPreviewMedia && presentation.showGenerating ? (
            <div className="pcv-gen-result-loading" data-testid="tryon-generating-empty">
              <LoadingOutlined spin />
              <span>{resultStatusBadge}</span>
            </div>
          ) : null}

          {showPreviewMedia && resultLoadError ? (
            <div className="pcv-gen-result-empty nodrag">
              <PictureOutlined />
              <span>{t('virtualTryOn.preview.unavailable')}</span>
              <button
                type="button"
                className="pcv-gen-action-btn"
                onClick={(e) => {
                  e.stopPropagation()
                  setResultLoadError(false)
                }}
              >
                {t('virtualTryOn.preview.retry')}
              </button>
            </div>
          ) : null}
        </div>

        {submitError || (errorMessage && (status === 'FAILED' || status === 'COMPLETED')) ? (
          <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error">
            {submitError || errorMessage}
          </div>
        ) : null}
      </div>

      {showPreviewMedia ? (
        <MediaWorkflowRail
          sourceNodeType="generate_tryon"
          className="pcv-media-workflow-toolbar--generate-tryon"
          onMouseEnter={activateResultZone}
          onMouseLeave={deactivateResultZone}
          onQuickGenerate={(kind) => {
            nodeData.onQuickGenerate?.(id, kind)
          }}
        />
      ) : null}

      {showExpanded ? (
        <div className="pcv-brief-body pcv-gen-input-body nodrag" onClick={(e) => e.stopPropagation()} data-testid="tryon-expanded-body">
          {/* ③ Input Assets — compact Person | Garment */}
          <div className="pcv-brief-section">
            <span className="pcv-brief-section-label">{t('virtualTryOn.assets.label', { defaultValue: '输入资产' })}</span>
            <div className="pcv-tryon-asset-grid">
              <div className="pcv-tryon-asset-card" data-testid="tryon-person-slot">
                <span className="pcv-gen-field-label">
                  {t('virtualTryOn.person.label')} ({hasPerson ? 1 : 0})
                </span>
                <div className="pcv-gen-field-chip">
                  <ImageThumbnailHoverPreview thumbUrl={personThumb} />
                  <span className="pcv-gen-field-chip-name">{personDisplayLabel}</span>
                  <button type="button" className="pcv-gen-link-btn" disabled={isBusy} onClick={openPersonPicker}>
                    {hasPerson ? t('virtualTryOn.person.replace') : t('virtualTryOn.person.select')}
                  </button>
                </div>
              </div>
              <div className="pcv-tryon-asset-card" data-testid="tryon-garment-slot">
                <span className="pcv-gen-field-label">
                  {t('virtualTryOn.garment.label')} ({hasGarment ? 1 : 0})
                </span>
                <div className="pcv-gen-field-chip">
                  <ImageThumbnailHoverPreview thumbUrl={garmentThumb} />
                  <span className="pcv-gen-field-chip-name">{garmentDisplayLabel}</span>
                  {!connectedGarment?.ready ? (
                    <button type="button" className="pcv-gen-link-btn" onClick={openGarmentPicker}>
                      {t('virtualTryOn.garment.select')}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {/* ④ Parameters — Outfit scope (provider-capability gated) */}
          <div className="pcv-gen-params-header">
            <span className="pcv-brief-section-label">{t('virtualTryOn.params.label', { defaultValue: '生成参数' })}</span>
          </div>
          <div className="pcv-gen-params-primary pcv-tryon-params-row">
            <div className="pcv-gen-field-row">
              <span className="pcv-gen-field-label">{t('virtualTryOn.outfitScope.label')}</span>
              {outfitScopeControls.outfit_scope_selector === 'HIDDEN' ? (
                <span className="pcv-gen-field-static" data-testid="tryon-outfit-scope-static">
                  {t('virtualTryOn.outfitScope.fullLookAuto', {
                    defaultValue: t('virtualTryOn.outfitScope.auto'),
                  })}
                </span>
              ) : (
                <CanvasSelect
                  size="small"
                  className="pcv-gen-field-select nodrag"
                  popupClassName="pcv-gen-field-select-popup"
                  data-testid="tryon-outfit-scope-select"
                  value={outfitScope === 'AUTO' ? 'FULL_LOOK_AUTO' : outfitScope}
                  disabled={isBusy}
                  options={outfitScopeControls.supported_outfit_scopes.map((scope) => ({
                    value: scope === 'AUTO' ? 'FULL_LOOK_AUTO' : scope,
                    label:
                      scope === 'AUTO' || scope === 'FULL_LOOK_AUTO'
                        ? t('virtualTryOn.outfitScope.fullLookAuto', {
                            defaultValue: t('virtualTryOn.outfitScope.auto'),
                          })
                        : scope === 'UPPER_ONLY'
                          ? t('virtualTryOn.outfitScope.upperOnly')
                          : scope === 'LOWER_ONLY'
                            ? t('virtualTryOn.outfitScope.lowerOnly')
                            : t('virtualTryOn.outfitScope.onePiece'),
                  }))}
                  onChange={(v: OutfitScope) => {
                    patchFields(
                      markStaleIfHasResult({
                        outfitScope: v === 'AUTO' ? 'FULL_LOOK_AUTO' : v,
                        outfitScopeSource: 'USER',
                        category: legacyCategoryForScope(v),
                      }),
                    )
                  }}
                  popupMatchSelectWidth={false}
                  placement="bottomLeft"
                  getPopupContainer={() => document.body}
                />
              )}
            </div>
            <div className="pcv-gen-field-hint" data-testid="tryon-outfit-scope-hint">
              {t('virtualTryOn.outfitScope.autoHint')}
            </div>
            {outfitScopeControls.capability_gap_code === 'CURRENT_PROVIDER_LIMITED_ACCESSORIES' ? (
              <div className="pcv-gen-field-hint" data-testid="tryon-outfit-scope-capability-gap">
                {t('virtualTryOn.outfitScope.providerLimitedAccessories')}
              </div>
            ) : null}
          </div>

          {/* ⑤ Prompt — last input section */}
          <div className="pcv-brief-section">
            <span className="pcv-brief-section-label">
              {t('virtualTryOn.prompt.label', { defaultValue: '补充说明（可选）' })}
            </span>
            <textarea
              className="pcv-brief-textarea nodrag"
              data-testid="tryon-prompt"
              placeholder={t('virtualTryOn.prompt.placeholder', {
                defaultValue: '请保持人物姿态和背景不变，自然融合服装材质、褶皱和光影……',
              })}
              value={prompt}
              maxLength={PROMPT_MAX_LENGTH}
              onChange={(e) => {
                patchFields(markStaleIfHasResult({ prompt: e.target.value }))
              }}
            />
            <span className="pcv-brief-textarea-counter">
              {prompt.length}/{PROMPT_MAX_LENGTH}
            </span>
          </div>

          {/* ⑥ Footer — Provider | Skill | Generate (no Diverge) */}
          <div className="pcv-gen-footer nodrag" onClick={(e) => e.stopPropagation()} data-testid="tryon-footer">
            <Tooltip
              title={providerSelectOptions.find((o) => o.value === resolvedProviderOptionId)?.title || t('virtualTryOn.mode.label')}
              mouseEnterDelay={0.4}
            >
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select pcv-gen-footer-select nodrag"
                popupClassName="pcv-gen-field-select-popup pcv-tryon-provider-select-popup"
                data-testid="tryon-provider-select"
                aria-label={t('virtualTryOn.mode.label')}
                value={resolvedProviderOptionId ?? undefined}
                virtual={false}
                listHeight={360}
                disabled={isBusy || providerOptionsError || providerSelectOptions.length === 0}
                placeholder={
                  providerOptionsError ? t('virtualTryOn.provider.loadFailed') : t('virtualTryOn.mode.placeholder')
                }
                options={providerSelectOptions}
                optionRender={(option) => {
                  const optData = option.data as {
                    reasonLabel?: string | null
                    engineLabel?: string | null
                  }
                  return (
                    <div className="pcv-tryon-provider-option">
                      <div className="pcv-tryon-provider-option-name">{option.label}</div>
                      {optData.engineLabel ? (
                        <div className="pcv-tryon-provider-option-engine" data-testid="tryon-provider-engine-label">
                          {optData.engineLabel}
                        </div>
                      ) : null}
                      {optData.reasonLabel ? <div className="pcv-tryon-provider-option-reason">{optData.reasonLabel}</div> : null}
                    </div>
                  )
                }}
                onChange={(v) => {
                  patchFields(markStaleIfHasResult(buildExplicitProviderSelectionPatch(v)))
                }}
                popupMatchSelectWidth={false}
                placement="bottomLeft"
                getPopupContainer={() => document.body}
              />
            </Tooltip>
            <CanvasSelect
              size="small"
              className="pcv-gen-field-select pcv-gen-footer-select nodrag"
              popupClassName="pcv-gen-field-select-popup"
              data-testid="tryon-skill-select"
              value={skillSelectionSource === 'EXPLICIT' ? 'virtual_tryon' : 'AUTO'}
              options={[
                { value: 'AUTO', label: t('virtualTryOn.skill.auto', { defaultValue: '自动（推荐）' }) },
                { value: 'virtual_tryon', label: t('virtualTryOn.skill.v1', { defaultValue: '虚拟换装 v1' }) },
              ]}
              onChange={(v) =>
                patchFields(
                  markStaleIfHasResult(
                    v === 'AUTO'
                      ? { skillSelectionSource: 'AUTO', skillKey: null }
                      : { skillSelectionSource: 'EXPLICIT', skillKey: v },
                  ),
                )
              }
              popupMatchSelectWidth={false}
              placement="bottomLeft"
              getPopupContainer={() => document.body}
            />
            <button
              type="button"
              ref={runBtnRef}
              className="pcv-gen-footer-generate-btn"
              data-testid="tryon-generate-btn"
              disabled={!canGenerate}
              onClick={handleGenerateClick}
            >
              {isBusy ? <LoadingOutlined spin /> : <ThunderboltOutlined />}{' '}
              {isBusy ? t('virtualTryOn.actions.generating') : t('virtualTryOn.actions.generate')}
            </button>
          </div>
          {explicitProviderInvalid ? (
            <div className="pcv-gen-field-hint pcv-tryon-provider-unavailable" data-testid="tryon-provider-unavailable">
              {t('virtualTryOn.provider.currentUnavailable', {
                defaultValue: '当前 Provider 已不可用，请重新选择。推荐：{{name}}',
                name: recommendedProviderLabel,
              })}
            </div>
          ) : null}
          {providerParameterHints.length > 0 ? (
            <div className="pcv-gen-field-hint" data-testid="tryon-provider-parameter-schema">
              {t('virtualTryOn.provider.parameterSchemaHint', {
                defaultValue: '当前 Provider 参数：{{params}}',
                params: providerParameterHints.join(', '),
              })}
            </div>
          ) : null}
          {providerInputConstraintHint ? (
            <div className="pcv-gen-field-hint" data-testid="tryon-provider-input-constraints">
              {t('virtualTryOn.provider.inputConstraintPrefix', {
                defaultValue: '发送给当前 Provider 时将自动优化（原图不会被修改）：{{hint}}',
                hint: providerInputConstraintHint,
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      <CanvasActionPopover
        anchorEl={historyBtnEl}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={t('virtualTryOn.history.title', { defaultValue: '生成历史' })}
        width={280}
      >
        {generationHistory.length === 0 ? (
          <div className="pcv-gen-history-empty">{t('virtualTryOn.history.empty', { defaultValue: '暂无历史版本' })}</div>
        ) : (
          <div className="pcv-gen-history-list" data-testid="tryon-history-list">
            {[...generationHistory].reverse().map((h) => (
              <button
                key={`${h.version}-${h.generatedOutputId || h.outputAssetId}`}
                type="button"
                className="pcv-gen-history-item nodrag"
                onClick={() => restoreHistoryEntry(h)}
              >
                <span className="pcv-gen-history-version">V{h.version}</span>
                <span className="pcv-gen-history-meta">
                  {h.completedAt ? new Date(h.completedAt).toLocaleString() : ''}
                </span>
              </button>
            ))}
          </div>
        )}
      </CanvasActionPopover>

      <CanvasActionPopover
        anchorEl={runBtnRef.current}
        open={regenerateChoiceOpen}
        onClose={() => setRegenerateChoiceOpen(false)}
        title={t('virtualTryOn.regenerate.title', { defaultValue: regenerate.title })}
        width={300}
      >
        <p className="pcv-action-popover-desc">{t('generation.regenerateDescription')}</p>
        <div className="pcv-action-popover-actions" data-testid="tryon-regen-decision">
          <button
            type="button"
            className="pcv-action-popover-btn nodrag"
            data-testid="tryon-regen-as-new"
            onClick={handleRegenerateSeparately}
          >
            {t('virtualTryOn.regenerate.separate', { defaultValue: regenerate.separate })}
          </button>
          <button
            type="button"
            className="pcv-action-popover-btn pcv-action-popover-btn--primary nodrag"
            data-testid="tryon-regen-overwrite"
            onClick={handleOverwriteRegenerate}
          >
            {t('virtualTryOn.regenerate.overwrite', { defaultValue: regenerate.overwrite })}
          </button>
        </div>
      </CanvasActionPopover>

      {previewOpen && showPreviewMedia && resultUrl ? (
        <MediaLightbox
          open
          kind="image"
          src={resultUrl}
          masterSrc={masterUrl}
          masterMeta={{
            width: (masterMetaExtra?.width && masterMetaExtra.width > 0
              ? masterMetaExtra.width
              : outputWidth > 0
                ? outputWidth
                : null),
            height: (masterMetaExtra?.height && masterMetaExtra.height > 0
              ? masterMetaExtra.height
              : outputHeight > 0
                ? outputHeight
                : null),
            sizeBytes: masterMetaExtra?.sizeBytes ?? null,
            createdAt: masterMetaExtra?.createdAt ?? null,
            origin: 'generated',
          }}
          onClose={() => setPreviewOpen(false)}
        />
      ) : null}
    </div>
  )
}

export default memo(GenerateTryOnNodeImpl)
